import React from "react";
import { useSubscription } from "../contexts/SubscriptionContext";
import { APP_CONFIG } from "../config/app.config";

interface FreeTrialStatusProps {
    freeRunsRemaining: number;
}

export const FreeTrialStatus: React.FC<FreeTrialStatusProps> = ({ freeRunsRemaining }) => {
    const { openSubscriptionModal } = useSubscription();

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Free Trial Status
            </h2>

            {/* Free Runs Counter */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-6 text-center">
                <div className="mb-4">
                    <div className="text-5xl font-bold text-green-600 mb-2">
                        {freeRunsRemaining}
                    </div>
                    <p className="text-lg text-green-800 font-medium">
                        Free AI Meal Plans Remaining
                    </p>
                </div>
                
                {freeRunsRemaining === 0 ? (
                    <p className="text-sm text-green-700 mb-4">
                        You've used all your free meal plans
                    </p>
                ) : (
                    <p className="text-sm text-green-700 mb-4">
                        Generate {freeRunsRemaining} more meal {freeRunsRemaining === 1 ? 'plan' : 'plans'} for free
                    </p>
                )}

                <div className="bg-green-100 rounded p-3 mb-4">
                    <p className="text-xs text-green-800">
                        <strong>Free Trial Active</strong> • No charges until you subscribe
                    </p>
                </div>
                
                {freeRunsRemaining > 0 && (
                    <a
                        href="/meal-planner/generate"
                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transform transition-all duration-200 hover:scale-105"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Generate A Meal Plan
                    </a>
                )}
            </div>

            {/* Upgrade CTA */}
            <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Ready for Unlimited Meal Planning?
                </h3>
                <p className="text-gray-600 mb-6">
                    Subscribe to get unlimited AI meal plans, advanced features, and more
                </p>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-2xl font-bold text-primary-600 mb-1">
                        ${APP_CONFIG.SUBSCRIPTION_PRICE}/month
                    </p>
                    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        {APP_CONFIG.SUBSCRIPTION_DISCOUNT_PERCENT}% OFF regular pricing
                    </span>
                </div>

                <ul className="text-left space-y-2 mb-6 max-w-sm mx-auto">
                    <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">Unlimited AI meal plan generation</span>
                    </li>
                    <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">Regenerate individual meals & days</span>
                    </li>
                    <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">Advanced customization options</span>
                    </li>
                    <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">4 million tokens per month</span>
                    </li>
                </ul>

                <button
                    onClick={openSubscriptionModal}
                    className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    Subscribe Now
                </button>
            </div>
        </div>
    );
}; 